import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, type DefaultValues } from 'react-hook-form';

import {
  fitnessQuestionnaireSchema,
  fitnessQuestionnaireService,
} from '@personal-trainer-app/shared';
import {
  createQuestionnaireDefaults,
  getRefineSavedMessage,
  needsPlanReview,
  PLAN_REVIEW_COPY,
  savedAnswersToFormValues,
} from '@personal-trainer-app/shared/src/utils/questionnaire/questionnaire-form';

import { RefinePlanForm } from '../RefinePlanForm';

import type { RefinePlanModalProps } from './RefinePlanModal.types';
import type { FitnessQuestionnaireInput } from '@personal-trainer-app/shared';

import { Alert, Button } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/auth';
import {
  useSavedQuestionnaire,
  type SavedQuestionnaireAnswers,
} from '@/hooks/questionnaire';
import { useAuthStore } from '@/stores';

const DEFAULT_VALUES: DefaultValues<FitnessQuestionnaireInput> =
  createQuestionnaireDefaults();

/** The saved answers (or none → a blank form) as the form's values. */
const toFormValues = (
  answers: SavedQuestionnaireAnswers | null
): DefaultValues<FitnessQuestionnaireInput> =>
  savedAnswersToFormValues(answers);

/**
 * RefinePlanModal — the returning-user "Adjust goals / refine plan" surface.
 *
 * Loads the user's current answers (`GET ?template=true`), pre-fills the full
 * editable criteria, and on submit posts the whole payload. Generation is
 * additive (a new plan; existing plans stay); live progress is owned by the
 * notification bell, so on success we just confirm and close — no progress UI
 * lives here (PRD §5).
 *
 * A `Modal`, which becomes a full-height bottom sheet below `md` on its own.
 */
export function RefinePlanModal({
  isOpen,
  onClose,
}: RefinePlanModalProps): React.JSX.Element {
  const methods = useForm<FitnessQuestionnaireInput>({
    resolver: zodResolver(fitnessQuestionnaireSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });
  const { reset, handleSubmit, watch } = methods;
  // A plan around an injury is checked before release: the button says so.
  const heldForReview = needsPlanReview({
    has_limitations: watch('has_limitations'),
  });

  const { data, hasFetched, error: loadError } = useSavedQuestionnaire(isOpen);
  const { success } = useToast();
  const { user } = useAuth();
  const fetchUser = useAuthStore((state) => state.fetchUser);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Pre-fill once the saved answers (or a 404 → fresh) resolve. Keyed on the
  // fetched `data` identity so a re-opened modal re-fills, but typing isn't
  // clobbered while the same data stays loaded.
  const lastFilled = useRef<SavedQuestionnaireAnswers | null | undefined>(
    undefined
  );
  useEffect(() => {
    if (!isOpen) {
      lastFilled.current = undefined;
      return;
    }
    if (hasFetched && lastFilled.current !== data) {
      reset(toFormValues(data));
      lastFilled.current = data;
    }
  }, [isOpen, hasFetched, data, reset]);

  const submit = useCallback(
    (generate: boolean) =>
      handleSubmit(async (values) => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
          await fitnessQuestionnaireService.submit(values, {
            generatePersonalizedPlan: generate,
          });
          success(
            getRefineSavedMessage(values, generate, user?.plan_review?.status)
          );
          // The review follows the answers just saved: read it again.
          void fetchUser();
          onClose();
        } catch (err) {
          setSubmitError(
            err instanceof Error
              ? err.message
              : 'Something went wrong. Please try again.'
          );
        } finally {
          setIsSubmitting(false);
        }
      }),
    [handleSubmit, success, onClose, user?.plan_review?.status, fetchUser]
  );

  const loading = isOpen && !hasFetched;

  const footer = (
    <>
      <Button
        variant="ghost"
        onClick={submit(false)}
        disabled={isSubmitting || loading}
      >
        Save without generating
      </Button>
      <Button
        variant="primary"
        onClick={submit(true)}
        loading={isSubmitting}
        disabled={loading}
        data-testid="refine-generate"
      >
        {heldForReview ? PLAN_REVIEW_COPY.refineLabel : 'Save & generate plan'}
      </Button>
    </>
  );

  const body = loading ? (
    <p className="py-12 text-center text-neutral-400">Loading your answers…</p>
  ) : (
    <>
      <p className="mb-6 text-body-sm text-neutral-400">
        Update anything and we&apos;ll build a fresh plan from your current
        goals — your existing plans stay.
      </p>
      {(submitError ?? loadError) && (
        <div className="mb-4">
          <Alert variant="error">{submitError ?? loadError}</Alert>
        </div>
      )}
      <RefinePlanForm />
    </>
  );

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        title="Refine your plan"
        sheetHeight="full"
        // Guard the in-progress refine form from an accidental dismiss — close
        // via the ✕ or the footer actions, not a stray backdrop tap (or, as a
        // sheet, a swipe-down).
        closeOnBackdropClick={false}
        footer={footer}
      >
        {body}
      </Modal>
    </FormProvider>
  );
}

export default RefinePlanModal;
